<template>
  <layout>
    <div
      class="py-4 px-8 bg-white flex flex-col gap-4 items-stretch justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <h1 class="text-gray-900 text-xl font-medium">
            {{ t("admin.settings.documents.documents") }}
          </h1>
        </div>
        <template v-if="hasPermission(permissions.createDocument)">
          <primary-button
            class="hidden sm:flex"
            :label="t('admin.settings.documents.add_document')"
            @click="handleOpenAddDocument"
          />
          <PlusIcon
            class="w-10 text-gray-900 sm:hidden -mr-1"
            @click="handleOpenAddDocument"
          />
        </template>
      </div>
      <!-- Filters -->
      <div class="relative z-30">
        <Filters :filters="filters" filterStoreKey="documentFilters" />
      </div>
    </div>
    <div class="flex flex-col overflow-auto">
      <DataTable
        :requestUrl="route('api.documents.search')"
        :transformFilters="transformFilters"
        :columns="columns"
        filterStoreKey="documentFilters"
        :selectedRowKey="selectedDocument?.id"
        :emptyLabel="t('components.empty_states.documents.title')"
        :emptyDescription="t('components.empty_states.documents.description')"
        :emptyAction="
          hasPermission(permissions.createDocument)
            ? () => handleOpenAddDocument()
            : null
        "
        :emptyActionLabel="t('components.empty_states.documents.button')"
        emptyImage="/images/empty/documents.svg"
        :canDelete="hasPermission(permissions.deleteDocument)"
        :canUpdate="hasPermission(permissions.updateDocument)"
        :actions="actions"
        @rowSelected="handleDocumentSelected"
        @delete="handleDeleteDocument"
      />
    </div>

    <add-documents-dialog
      :fields="fields"
      :open="openAddDocument"
      @close="handleCloseAddDocument"
    />

    <!-- Editing Documents -->
    <settings-panel
      :open="openDocumentsSlideOver"
      :details="parsedDocumentDetails"
      :fields="fields"
      :options="actions"
      :canCreate="hasPermission(permissions.createDocument)"
      :canUpdate="hasPermission(permissions.updateDocument)"
      :canDelete="hasPermission(permissions.deleteDocument)"
      :updateLabel="t('admin.settings.documents.update.title')"
      @close="handleCloseDocumentsSlideOver"
      @update="handleUpdateDocument"
      @delete="handleDeleteDocument"
    >
      <template v-slot:after>
        <DocumentFile
          :file="parsedDocumentDetails.file"
          @download="downloadFile(parsedDocumentDetails)"
          @view="downloadFile(parsedDocumentDetails, true)"
        />
      </template>
    </settings-panel>
  </layout>
</template>

<script setup>
import { computed, defineProps, inject, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import axios from "axios";

import Layout from "@/Tailwind/Pages/Settings";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import { useUTC } from "@/Tailwind/Mixins/useUTC";
import AddDocumentsDialog from "@/Tailwind/Components/Documents/AddDocumentsDialog.vue";
import { usePage } from "@inertiajs/vue3";
import SettingsPanel from "@/Tailwind/Components/Settings/SettingsPanel.vue";
import Filters from "@/Tailwind/Components/Filters.vue";
import DataTable from "@/Tailwind/Components/Table/DataTable.vue";
import { useEventBus } from "@/Tailwind/Mixins/useEventBus";
import Eye from "@/Tailwind/Icon/Eye.vue";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { mobileSatisfiesVersion } from "@/Tailwind/Helpers/version";
import DocumentFile from "@/Tailwind/Components/Documents/DocumentFile.vue";
import { usePreloadStore } from "@/Tailwind/Stores/preload";

const props = defineProps({
  note: Object,
});

const { t } = useI18n();

const { emit } = useEventBus();

const { hasPermission, permissions } = inject("permissions");

const preloadStore = usePreloadStore();
const spaceHierarchy = computed(() => preloadStore.locations);
const assetHierarchy = computed(() => preloadStore.assets);

// Filters
const filters = computed(() => [
  {
    key: "spaces",
    type: "hierarchy",
    options: spaceHierarchy,
    label: t("admin.search.filters.space"),
    placeholder: t("components.inputs.searchLocations"),
  },
  {
    key: "assets",
    type: "hierarchy",
    options: assetHierarchy,
    label: t("admin.search.filters.asset"),
    placeholder: t("components.inputs.search"),
  },
  {
    key: "expiration_date",
    type: "date",
    label: t("admin.settings.documents.expiration_date.title"),
    transform: (dates) => ({ gte: dates[0], lte: dates[1] }),
  },
  {
    key: "notification_date",
    type: "date",
    label: t("admin.settings.documents.notification_date.title"),
    transform: (dates) => ({ gte: dates[0], lte: dates[1] }),
  },
]);

const transformFilters = (filters) => {
  const transformedFilters = {
    ...filters,
    space: [...(filters.spaces ?? []), ...(filters.assets ?? [])],
  };

  delete transformedFilters.spaces;
  delete transformedFilters.assets;

  return transformedFilters;
};

// Data table
const columns = computed(() => [
  {
    key: "title",
    label: t("admin.settings.documents.columns.title"),
    sortable: true,
  },
  {
    key: "description",
    label: t("admin.settings.documents.columns.description"),
    sortable: true,
    classes: "text-gray-500",
  },
  {
    key: "expiration_date",
    label: t("admin.settings.documents.columns.expiration_date"),
    sortable: true,
    type: "date",
  },
  {
    key: "spaces",
    label: t("admin.settings.documents.columns.space"),
    type: "space",
  },
  {
    key: "public",
    label: t("admin.settings.documents.columns.public"),
    type: "visibility",
    onSelected: handleVisibilitySelected,
  },
]);

const handleVisibilitySelected = (documentId, visibility) => {
  axios.post(route("api.files.visibility", [documentId]), {
    public: visibility === "public",
  });
};

const { fromUTC } = useUTC();

const openAddDocument = ref();
const openDocumentsSlideOver = computed(() => !!props.note);

const selectedDocument = computed(() => props.note);

const parsedDocumentDetails = computed(() => {
  if (!selectedDocument.value) {
    return;
  }

  const spaces = selectedDocument.value.spaces.map((space) => space.id);

  return {
    ...selectedDocument.value,
    spaces,
    has_expiration_date: !!selectedDocument.value.expiration_date,
    expiration_date: selectedDocument.value.expiration_date
      ? fromUTC(selectedDocument.value.expiration_date)
      : undefined,
    reminder_date: selectedDocument.value.reminder_date
      ? fromUTC(selectedDocument.value.reminder_date)
      : undefined,
  };
});

function handleOpenAddDocument() {
  openAddDocument.value = true;
}

const fields = computed(() => [
  {
    key: "title",
    title: t("admin.settings.documents.title.title"),
    placeholder: t("admin.settings.documents.title.placeholder"),
    type: "text",
    required: true,
  },
  {
    key: "description",
    title: t("admin.settings.documents.description.title"),
    placeholder: t("admin.settings.documents.description.placeholder"),
    type: "textarea",
  },
  {
    title: t("admin.settings.documents.tenant.title"),
    key: "tenant",
    type: "dropdown",
    required: true,
    preFilled: true,
    options: computed(() =>
      usePage().props.tenants.map((tenant) => ({
        label: tenant.name,
        value: tenant.id,
      }))
    ),
    show: () => !selectedDocument.value,
  },
  {
    title: t("admin.settings.documents.location.title"),
    key: "spaces",
    type: "location",
    required: true,
    extraAttributes: {
      selectorStyle: "dropdown",
      multiple: true,
      disableUngrouped: true,
    },
    injectFormData: {
      tenants: (form) => {
        if (selectedDocument.value) {
          return [selectedDocument.value.spaces?.[0]?.tenant_id];
        }

        return form.tenant ? [form.tenant] : null;
      },
      disabled: (form) => {
        if (
          selectedDocument.value &&
          !hasPermission(permissions.updateDocument)
        ) {
          return true;
        }

        return !form.tenant && !selectedDocument.value;
      },
    },
  },
  {
    key: "public",
    title: t("admin.settings.documents.public.title"),
    description: t("admin.settings.documents.public.description"),
    type: "switch",
  },
  {
    key: "has_expiration_date",
    title: t("admin.settings.documents.expiration_reminder.title"),
    description: t("admin.settings.documents.expiration_reminder.description"),
    type: "switch",
    fields: [
      {
        type: "composite",
        fields: [
          {
            key: "expiration_date",
            title: t("admin.settings.documents.expiration_date.title"),
            type: "date",
            extraAttributes: {
              hint: t("admin.settings.documents.expiration_date.description"),
              minDate: new Date(),
            },
          },
          {
            key: "reminder_date",
            title: t("admin.settings.documents.notification_date.title"),
            type: "date",
            extraAttributes: {
              hint: t("admin.settings.documents.notification_date.description"),
              minDate: new Date(),
            },
            injectFormData: {
              maxDate: (form) => {
                return new Date(form?.expiration_date);
              },
              disabled: (form) => {
                return !form?.expiration_date;
              },
            },
          },
        ],
      },
    ],
  },
]);

const { runningInWebView, openURL } = useWebView();
const downloadFile = (document, preview) => {
  const url = preview ? document.file.urls.file : document.file.urls.download;

  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  // Fallback for outdated mobile apps.
  window.open(url, "_blank");
};

const actions = [
  {
    key: "delete",
    label: t("admin.settings.documents.actions.view"),
    icon: Eye,
    onClick: downloadFile,
  },
];

function handleUpdateDocument(inertiaForm) {
  if (!selectedDocument.value) {
    return;
  }

  inertiaForm
    .transform((data) => ({
      ...data,
      space_ids: data.spaces,
    }))
    .put(
      route("settings.organisation.notes.update", {
        note: selectedDocument.value.id,
      }),
      {
        onSuccess: () => {
          handleCloseDocumentsSlideOver(true);
        },
      }
    );
}

function handleDeleteDocument(id) {
  if (!selectedDocument.value && !id) {
    return;
  }

  router.delete(
    route("settings.organisation.notes.destroy", {
      note: id ?? selectedDocument.value?.id,
    }),
    {
      onSuccess: () => {
        handleCloseDocumentsSlideOver(true);
      },
    }
  );
}

function handleCloseAddDocument(reload = false) {
  openAddDocument.value = false;

  toIndex(reload);
}

function handleCloseDocumentsSlideOver(reload = false) {
  toIndex(reload);
}

const handleDocumentSelected = (document) => {
  router.visit(route("settings.organisation.notes.show", [document]), {
    preserveState: true,
    preserveScroll: true,
  });
};

function toIndex(reload = false) {
  router.visit(route("settings.organisation.notes.index"), {
    preserveState: true,
    preserveScroll: true,
  });

  if (reload) {
    emit("reload-data-table");
  }
}

function handleBack() {
  router.visit(route("settings.organisation.index"));
}
</script>

<style lang="scss" scoped>
td {
  max-width: 20vw;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.dynamic-height {
  height: calc(100vh - 151px);
}

@media (min-width: 640px) {
  .dynamic-height {
    height: calc(100vh - 64px - 145px);
  }
}
</style>
